import { IonApp, IonRouterOutlet, IonToast } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { TimeoutId } from '@reduxjs/toolkit/dist/query/core/buildMiddleware/types';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router-dom';
import { RootState } from './App';
import { QuirkLoading } from './components/QuirkLoading';
import { LandingPage } from './pages/LandingPage';
import {
	LoginState,
	fetchDomain,
	fetchImageToken,
	fetchCommunities,
	fetchPlatform,
	refreshToken,
	setLoginMessage
} from './state/loginSlice';
import { SelectCommunityPage } from './pages/SelectCommunityPage.js';
import { useGetPublicInformationQuery } from './api/SiteSettingsAPI.js';
import { LicenseContext } from './hooks/useLiscenseFeature.js';

export const Main = () => {
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
	const { message, isLoading } = useSelector((state: RootState) => state.loading);
	const refreshRef = useRef<TimeoutId | null>(null);
	const { data: publicInfo } = useGetPublicInformationQuery();
	useEffect(() => {
		refreshRef.current = setInterval(() => {
			dispatch(refreshToken());
		}, 15 * 60 * 1000);
		const dispatchActions = async () => {
			await dispatch(fetchPlatform());
			await dispatch(fetchDomain());
			await dispatch(refreshToken());
			await dispatch(fetchImageToken());
		};
		dispatchActions();
		return () => {
			refreshRef.current && clearInterval(refreshRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!state.domain && state.platform && state.platform !== 'web') {
			dispatch(fetchCommunities());
		}
	}, [dispatch, state.domain, state.platform]);

	return (
		<LicenseContext.Provider value={publicInfo?.license}>
			<IonApp>
				<QuirkLoading message={message} isLoading={isLoading || !state.platform} />

				<IonReactRouter>
					{state.platform && state.platform !== 'web' && !state.domain && (
						<Route>
							<SelectCommunityPage />
						</Route>
					)}
					{(state.platform === 'web' || (state.domain && state.platform)) && (
						<IonRouterOutlet id="main">
							<Route path="/main">
								<LandingPage />
							</Route>
							<Route exact path="/">
								<Redirect to="/main/latest" />
							</Route>
							<Route exact path="/signup">
								<Redirect to="/main/settings/signup" />
							</Route>
							<Route path="/main/settings/community">
								<SelectCommunityPage />
							</Route>
						</IonRouterOutlet>
					)}
				</IonReactRouter>
				<IonToast
					isOpen={state.loginMessage !== ''}
					duration={1500}
					message={state.loginMessage}
					position="middle"
					onDidDismiss={() => dispatch(setLoginMessage(''))}
				/>
			</IonApp>
		</LicenseContext.Provider>
	);
};
